import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { EmailTemplate, EmailTemplatesService, tagClassName, displayTag } from "../services/emailTemplatesService";
import { selectAuthenticated } from "../store/slices/authUserSlice";
import { RootState } from "../store/store";

const TemplateDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isAuthenticated = useSelector((state: RootState) => selectAuthenticated(state));
  const [template, setTemplate] = useState<EmailTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"newsletter" | "lifecycle">("newsletter");
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    if (!id) return;
    EmailTemplatesService.getTemplate(Number(id))
      .then(setTemplate)
      .catch(() => setError("Failed to load template."))
      .finally(() => setLoading(false));
  }, [id]);

  const handleImport = async () => {
    if (!id) return;
    setImporting(true);
    setError(null);
    try {
      const result = await EmailTemplatesService.importTemplate(Number(id));
      const editionId = activeTab === "lifecycle"
        ? result.lifecycle_edition_id
        : result.newsletter_edition_id;
      navigate(`/newsletterEdition/${editionId}`);
    } catch (err: any) {
      const msg = err.response?.data?.error || "Failed to import template.";
      setError(msg);
    } finally {
      setImporting(false);
    }
  };

  const activeHtml = activeTab === "newsletter" ? template?.preview_html : template?.lifecycle_preview_html;

  return (
    <div>
      <div className="template-detail-page">
        <button className="template-detail-back" onClick={() => navigate("/templates")}>
          ← Back to templates
        </button>

        {loading && <p className="templates-status">Loading...</p>}
        {error && <p className="templates-status">{error}</p>}

        {template && (
          <>
            <div className="template-detail-header">
              <h1 className="template-detail-title">{template.title}</h1>
              {template.description && (
                <p className="template-detail-description">{template.description}</p>
              )}
              {template.tags.length > 0 && (
                <div className="template-detail-tags">
                  {template.tags.map(tag => (
                    <span key={tag} className={tagClassName(tag)}>{displayTag(tag)}</span>
                  ))}
                </div>
              )}
              {isAuthenticated && (
                <button
                  className="template-detail-import-btn"
                  onClick={handleImport}
                  disabled={importing}
                >
                  {importing ? "Importing..." : "Use This Template"}
                </button>
              )}
            </div>
            <div className="template-detail-tabs">
              <button
                className={`template-detail-tab${activeTab === "newsletter" ? " active" : ""}`}
                onClick={() => setActiveTab("newsletter")}
              >
                Newsletter
              </button>
              <button
                className={`template-detail-tab${activeTab === "lifecycle" ? " active" : ""}`}
                onClick={() => setActiveTab("lifecycle")}
              >
                Lifecycle
              </button>
            </div>
          </>
        )}
      </div>

      {template && (
        <div className="template-detail-preview">
          {activeHtml ? (
            <div dangerouslySetInnerHTML={{ __html: activeHtml }} />
          ) : (
            <p className="templates-status">No preview available.</p>
          )}
        </div>
      )}
    </div>
  );
};

export default TemplateDetail;
